import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { avatarSrc } from "@/lib/defaults";
import { ArrowUpRight, Undo2, ShieldCheck, Crown, UserRound, Search, Check, Clock } from "lucide-react";

export const ESCALATION_TARGETS = [
  { key: "moderator", label: "Modérateur", icon: ShieldCheck, hint: "Contenu, comportement, signalements, sanctions." },
  { key: "super_admin", label: "Super admin", icon: Crown, hint: "Décision finale, paiements, comptes, accès techniques." },
  { key: "user", label: "Une personne", icon: UserRound, hint: "Un collègue précis de l'équipe tickets." },
];
const ROLE_LABELS = { super_admin: "Super admin", manager: "Manager", moderator: "Modérateur", associate: "Associé", support: "Support" };
export const STALE_HOURS = 48;

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "");
const rolesLabel = (u) => (u.roles?.length ? u.roles : [u.role]).filter(Boolean).map((r) => ROLE_LABELS[r] || r).join(" · ");

// Pastille « ↗ Transféré à … / Assigné à … par … » (liste + détail). Reste visible une fois le ticket résolu (historique).
export const EscalationChip = ({ ticket, compact = false, testId }) => {
  const e = ticket?.escalation;
  if (!ticket?.escalated || !e) return null;
  const label = e.to_label || e.to;
  const verb = e.to === "user" ? "Assigné à" : "Transféré à";
  return (
    <span className="chip text-[10px] inline-flex items-center gap-1" style={{ background: "#EDE9FE", color: "#5B21B6", borderColor: "#C4B5FD" }} title={e.note ? `Note : ${e.note}` : undefined} data-testid={testId} data-to={e.to}>
      {e.to === "user" ? <UserRound size={10} /> : <ArrowUpRight size={10} />} {compact ? `${e.to === "user" ? "Assigné · " : ""}${label}` : `${verb} ${label} par ${e.by_name || "l'équipe"} · ${fmtDate(e.at)}`}
    </span>
  );
};

// Pastille « ⏰ 52 h sans réponse » : le membre attend l'équipe depuis ≥ 48 h (le cron horaire relance le responsable).
export const StaleChip = ({ ticket, testId, threshold = STALE_HOURS }) => {
  const h = ticket?.awaiting_hours || 0;
  if (ticket?.status !== "open" || h < threshold) return null;
  return (
    <span className="chip text-[10px] inline-flex items-center gap-1" style={{ background: "#FEE2E2", color: "#991B1B", borderColor: "#FCA5A5" }} data-testid={testId} data-hours={Math.floor(h)}>
      <Clock size={10} /> {Math.floor(h)} h sans réponse
    </span>
  );
};

// Liste des collègues (équipe « tickets : gérer ») avec recherche ; un seul sélectionné.
const StaffPicker = ({ value, onChange }) => {
  const [staff, setStaff] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/admin/tickets/staff").then(({ data }) => setStaff(data.staff || [])).catch(() => setStaff([])); }, []);
  const shown = (staff || []).filter((u) => !q.trim() || (u.name || "").toLowerCase().includes(q.trim().toLowerCase()) || rolesLabel(u).toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <div className="space-y-2" data-testid="ticket-staff-picker">
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "var(--yalola-text-muted)" }} />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Chercher un collègue (nom, rôle)…" className="pl-9" data-testid="ticket-staff-search" />
      </div>
      <div className="max-h-44 overflow-y-auto rounded-xl border divide-y" style={{ borderColor: "var(--yalola-border)" }}>
        {staff === null && <div className="p-3 text-xs" style={{ color: "var(--yalola-text-muted)" }}>Chargement…</div>}
        {staff !== null && shown.length === 0 && <div className="p-3 text-xs" style={{ color: "var(--yalola-text-muted)" }} data-testid="ticket-staff-empty">Aucun collègue trouvé.</div>}
        {shown.map((u) => (
          <button key={u.id} type="button" onClick={() => onChange(u)} className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-black/5" style={value?.id === u.id ? { background: "var(--yalola-primary-soft)" } : {}} aria-pressed={value?.id === u.id} data-testid={`ticket-staff-${u.id}`}>
            <img src={avatarSrc(u, 32)} alt="" className="h-8 w-8 rounded-full object-cover shrink-0" style={{ background: "var(--yalola-surface-alt)" }} />
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-semibold truncate">{u.name}{u.me ? " (moi)" : ""}</span>
              <span className="block text-[11px] truncate" style={{ color: "var(--yalola-text-muted)" }}>{rolesLabel(u)}</span>
            </span>
            {value?.id === u.id && <Check size={16} style={{ color: "var(--yalola-primary)" }} />}
          </button>
        ))}
      </div>
    </div>
  );
};

// Boutons « Transférer » / « Reprendre » + boîte de choix du rôle (ou du collègue) et de la note interne.
export const EscalationControls = ({ ticket, canManage, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [to, setTo] = useState("moderator");
  const [assignee, setAssignee] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  if (!ticket || ticket.status !== "open") return null;

  const call = async (body, okMsg) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/tickets/${ticket.id}/escalate`, body);
      toast.success(okMsg(data));
      setOpen(false); setNote(""); setAssignee(null);
      onChanged?.();
    } catch (err) { toast.error(err?.friendlyMessage || err?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  const submit = () => call(
    { to, note: note.trim(), ...(to === "user" ? { assignee_id: assignee?.id } : {}) },
    (d) => (to === "user" ? `Ticket assigné à ${assignee?.name}` : `Ticket transféré (${d.notified} compte${d.notified > 1 ? "s" : ""} prévenu${d.notified > 1 ? "s" : ""})`),
  );

  return (
    <>
      {ticket.escalated ? (
        <Button size="sm" variant="outline" className="rounded-full" disabled={!canManage || busy} onClick={() => call({ to: "" }, () => (ticket.escalation?.to === "user" ? "Ticket repris — assignation annulée" : "Ticket repris — transfert annulé"))} data-testid="ticket-escalation-cancel-btn">
          <Undo2 size={14} className="mr-1" /> Reprendre le ticket
        </Button>
      ) : (
        <Button size="sm" variant="outline" className="rounded-full" disabled={!canManage} onClick={() => setOpen(true)} data-testid="ticket-escalate-btn">
          <ArrowUpRight size={14} className="mr-1" /> Transférer
        </Button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="ticket-escalate-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ArrowUpRight size={18} /> Transférer ce ticket</DialogTitle>
            <DialogDescription>Le rôle ou le collègue choisi reçoit une notification avec un lien direct vers le ticket. Ta note reste interne : le membre ne la voit jamais.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-2" data-testid="ticket-escalate-targets">
            {ESCALATION_TARGETS.map(({ key, label, icon: Icon, hint }) => (
              <button key={key} type="button" onClick={() => setTo(key)} className="rounded-xl border p-3 text-left" style={to === key ? { borderColor: "var(--yalola-primary)", background: "var(--yalola-primary-soft)" } : { borderColor: "var(--yalola-border)" }} aria-pressed={to === key} data-testid={`ticket-escalate-to-${key}`}>
                <div className="flex items-center gap-1.5 font-semibold text-[13px] whitespace-nowrap"><Icon size={15} style={{ color: "var(--yalola-primary)" }} /> {label}</div>
                <div className="text-[11px] mt-1" style={{ color: "var(--yalola-text-muted)" }}>{hint}</div>
              </button>
            ))}
          </div>
          {to === "user" && <StaffPicker value={assignee} onChange={setAssignee} />}
          <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value.slice(0, 1000))} placeholder="Note interne (facultative) : pourquoi tu transfères, ce que tu as déjà vérifié…" data-testid="ticket-escalate-note" />
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)} data-testid="ticket-escalate-cancel">Annuler</Button>
            <Button className="pill-primary rounded-full" disabled={busy || (to === "user" && !assignee)} onClick={submit} data-testid="ticket-escalate-submit">
              <ArrowUpRight size={14} className="mr-1" /> {to === "user" ? "Assigner" : "Transférer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
